// client/src/utils/analytics.ts
"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */

import Cookies from 'js-cookie'; // Import js-cookie
import { apiRequest } from './api';
import { getUTMForAnalytics } from './utmTracking';

// Define interfaces for better type safety
interface UTMParameters {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}

interface UTMData extends UTMParameters {
  landing_page: string;
  referrer: string;
  timestamp: string;
}

interface EventProperties {
  [key: string]: unknown;
}

// UTM parameter extraction
export const getUTMParameters = (): UTMParameters => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || 'direct',
    utm_medium: params.get('utm_medium') || 'none',
    utm_campaign: params.get('utm_campaign') || 'none',
    utm_term: params.get('utm_term') || 'none',
    utm_content: params.get('utm_content') || 'none'
  };
};

// Store UTM parameters for user
export const storeUTMParameters = async (userId: string) => {
  const utmParams = getUTMForAnalytics();
  const utmData: UTMData = {
    utm_source: utmParams.utm_source || 'direct',
    utm_medium: utmParams.utm_medium || 'none',
    utm_campaign: utmParams.utm_campaign || 'none',
    utm_term: utmParams.utm_term || 'none',
    utm_content: utmParams.utm_content || 'none',
    landing_page: window.location.pathname,
    referrer: document.referrer || 'direct',
    timestamp: new Date().toISOString()
  };

  // Cookie holds acquisition context only - user_id is sent in the POST body
  // below and persisted server-side, never echoed back to the client.
  Cookies.set('utm_data', JSON.stringify(utmData), { expires: 30 });
  
  // Send to server
  try {
    // Unauthenticated callers are fine here, but a signed-in one carries the
    // session cookie, which makes this POST subject to the CSRF check - so it
    // needs the same transport as everything else.
    await apiRequest('POST', '/analytics/acquisition', {
      userId,
      utmData: utmData
    });
  } catch (error) {
    console.error('Error storing UTM data:', error);
  }
  
  return utmData;
};

// Get stored UTM data
export const getStoredUTMData = (): UTMData | null => {
  const stored = Cookies.get('utm_data');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

// DEL-1045 (re-do): guard on the same signal the server authenticates on,
// not a nearby proxy. The first version (`hasSupabaseBrowserSession`
// probing localStorage for `sb-*-auth-token`) silently dropped events for
// signed-in email/password users, because the email path never writes the
// Supabase key — `AuthContext.signInWithEmail` calls `setCustomToken` only,
// and `server/routes/analytics.js` reads `Authorization`, not localStorage.
// `getAccessToken()` already returns the bearer for both auth models
// (`cachedSupabaseToken ?? cachedCustomToken`) and mirrors what
// `authenticateToken` accepts server-side. Use that.
import { getAccessToken } from './accessToken';
import { getPostHogSessionId } from '@/analytics/posthog';

// Analytics event tracking
export const trackEvent = async (eventName: string, properties: EventProperties = {}): Promise<void> => {
  try {
    // Server-captured events (e.g. user_signed_up) otherwise have no session,
    // so they could never be attributed to the page the visitor landed on.
    const sessionId = getPostHogSessionId?.();
    const eventData = {
      eventName,
      properties: {
        ...properties,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...(sessionId ? { $session_id: sessionId } : {}),
      }
    };

    // DEL-1045: skip the POST when there is no auth bearer. The session is
    // an HttpOnly cookie the client cannot read, but `getAccessToken()`
    // already knows both auth models (Supabase session for OAuth /
    // email-link; custom JWT for email/password, which never writes the
    // Supabase localStorage key) — and is the same signal the server's
    // `authenticateToken` accepts via `Authorization`. Skipping it drops
    // the `[RequestMonitor] Failed POST /api/analytics/event - 401`
    // console.error and the matching PostHog session-recording noise
    // without dropping events for any signed-in user. The local
    // pending-events queue is still populated so that an email-link
    // landing that signs in later can flush via retryPendingEvents().
    if (typeof window !== 'undefined' && !getAccessToken()) {
      storeEventLocally(eventName, properties);
      return;
    }

    await apiRequest('POST', '/analytics/event', eventData);
    console.log('Analytics event tracked:', eventName);
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // Analytics failures are never fatal - log and queue for retry.
    if (status === 403) {
      console.warn('Analytics API authorization failed (403) - event tracking disabled');
    } else if (status) {
      console.warn(`Analytics API returned ${status} - event tracking failed`);
    } else {
      console.error('Error tracking analytics event:', error);
    }
    storeEventLocally(eventName, properties);
    return;
  }
};

// Store event locally for retry
const storeEventLocally = (eventName: string, properties: EventProperties) => {
  try {
    const utmData = getStoredUTMData();
    const eventData = {
      event_name: eventName,
      properties: {
        ...properties,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        utm_source: utmData?.utm_source || 'direct',
        utm_medium: utmData?.utm_medium || 'none'
      }
    };

    // Store in Cookies for retry
    let existingEvents: any[] = [];
    try {
      existingEvents = JSON.parse(Cookies.get('pending_analytics_events') || '[]');
    } catch {
      existingEvents = [];
    }
    existingEvents.push(eventData);

    // Keep only last 100 events
    if (existingEvents.length > 100) {
      existingEvents.splice(0, existingEvents.length - 100);
    }

    // ponytail: hard 3 KB cap on the serialized payload so the cookie can be
    // sent on the next request without triggering 431. Cookie attributes
    // (name, expires, path) eat ~120 bytes of the 4 KB single-cookie budget,
    // and a ~1 KB safety margin prevents edge cases. If we overflow, drop the
    // oldest events rather than skipping the new one - analytics is best-effort.
    const MAX_PENDING_BYTES = 3 * 1024;
    let serialized = JSON.stringify(existingEvents);
    while (serialized.length > MAX_PENDING_BYTES && existingEvents.length > 1) {
      existingEvents.shift();
      serialized = JSON.stringify(existingEvents);
    }

    if (serialized.length <= MAX_PENDING_BYTES) {
      Cookies.set('pending_analytics_events', serialized, { expires: 7 });
    }
    return eventData;
  } catch (error) {
    console.error('Error storing event locally:', error);
  }
};

// Onboarding funnel events
export const trackOnboardingStep = async (stepName: string, additionalData: EventProperties = {}) => {
  const utmParams = getUTMForAnalytics();
  try {
    await apiRequest('POST', '/analytics/onboarding', {
      stepName,
      properties: {
        ...additionalData,
        ...utmParams
      }
    });

    console.log('Onboarding step tracked:', stepName);
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // Consistent with trackEvent: never fatal, always queued for retry.
    if (status === 403) {
      console.warn('Analytics API authorization failed (403) - onboarding tracking disabled');
    } else if (status) {
      console.warn(`Analytics API returned ${status} - onboarding tracking failed`);
    } else {
      console.error('Error tracking onboarding step:', error);
    }
    storeOnboardingStepLocally(stepName, { ...additionalData, ...utmParams });
    return null;
  }
};

// Store onboarding step locally for retry
const storeOnboardingStepLocally = (stepName: string, properties: EventProperties) => {
  try {
    const eventData = {
      step_name: stepName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString()
      }
    };

    let existingEvents: any[] = [];
    try {
      existingEvents = JSON.parse(Cookies.get('pending_onboarding_steps') || '[]');
    } catch {
      existingEvents = [];
    }
    existingEvents.push(eventData);

    // Keep only last 100 events
    if (existingEvents.length > 100) {
      existingEvents.splice(0, existingEvents.length - 100);
    }

    // ponytail: hard 3 KB cap; see storeEventLocally for the rationale.
    const MAX_PENDING_BYTES = 3 * 1024;
    let serialized = JSON.stringify(existingEvents);
    while (serialized.length > MAX_PENDING_BYTES && existingEvents.length > 1) {
      existingEvents.shift();
      serialized = JSON.stringify(existingEvents);
    }

    if (serialized.length <= MAX_PENDING_BYTES) {
      Cookies.set('pending_onboarding_steps', serialized, { expires: 7 });
    }
  } catch (error) {
    console.error('Error storing onboarding step locally:', error);
  }
};

// Retry pending onboarding steps
export const retryPendingOnboardingSteps = async () => {
  try {
    const stored = Cookies.get('pending_onboarding_steps');
    if (!stored) return 0;

    const pendingEvents = JSON.parse(stored);

    let successCount = 0;
    for (const event of pendingEvents) {
      try {
        // Throws on any non-2xx, so a replayed step only counts once the
        // server has actually accepted it.
        await apiRequest('POST', '/analytics/onboarding', {
          stepName: event.step_name,
          properties: event.properties
        });
        successCount++;
      } catch (_error) {
        console.error('Failed to retry onboarding step:', event.step_name, _error);
      }
    }

    // Remove successful events from pending
    if (successCount > 0) {
      const remainingEvents = pendingEvents.slice(successCount);
      Cookies.set('pending_onboarding_steps', JSON.stringify(remainingEvents), { expires: 7 });
    }

    return successCount;
  } catch (error) {
    console.error('Error retrying pending onboarding steps:', error);
    return 0;
  }
};

// User engagement events
export const trackUserEngagement = async (sessionTime = 0, pagesViewed = 0, actionsTaken = 0) => {
  try {
    const result = await apiRequest<{ data?: unknown }>('POST', '/analytics/engagement', {
      sessionTime,
      pagesViewed,
      actionsTaken
    });

    return result.data;
  } catch (error) {
    console.error('Error updating engagement:', error);
    return null;
  }
};

// Financial events
export const trackFinancialEvent = async (eventType: string, portfolioId: string | null = null, details: EventProperties = {}) => {
  try {
    const result = await apiRequest<{ data?: unknown }>('POST', '/analytics/financial', {
      portfolioId,
      eventType,
      eventData: details
    });

    console.log('Financial event tracked:', eventType);
    return result.data;
  } catch (error) {
    console.error('Error tracking financial event:', error);
    return null;
  }
};

// Get analytics events for admin dashboard
export const getAnalyticsEvents = (): unknown[] => {
  try {
    const stored = Cookies.get('pending_analytics_events');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting analytics events:', error);
    return [];
  }
};

// Retry pending events
export const retryPendingEvents = async () => {
  const pendingEvents = getAnalyticsEvents();
  const successfulEvents: unknown[] = []; // Explicitly type as unknown[]

  for (const event of pendingEvents) {
    try {
      await trackEvent((event as { event_name: string; properties: EventProperties }).event_name, (event as { event_name: string; properties: EventProperties }).properties);
      successfulEvents.push(event);
    } catch (_error) {
      console.error('Failed to retry event:', (event as { event_name: string }).event_name, _error);
    }
  }
  
  // Remove successful events from pending
  const remainingEvents = pendingEvents.filter(event =>
    !successfulEvents.some(success => (success as { event_name: string }).event_name === (event as { event_name: string }).event_name &&
    (success as { properties: { timestamp: string } }).properties.timestamp === (event as { properties: { timestamp: string } }).properties.timestamp)
  );
  
  Cookies.set('pending_analytics_events', JSON.stringify(remainingEvents), { expires: 7 });
  return successfulEvents.length;
};

// Specific tracking functions

export const trackReferralSignupCompleted = (referralCode: string, source: string) => {
  const utmParams = getUTMForAnalytics();
  trackEvent('referral_signup_completed', { 
    referral_code: referralCode,
    source,
    ...utmParams
  });
};

export const trackFirstAssetAdded = (assetName: string, category: string) => {
  const utmParams = getUTMForAnalytics();
  markActivationMilestone('asset');
  trackEvent('first_asset_added', {
    asset_name: assetName,
    asset_category: category,
    ...utmParams
  });
};

export const trackDashboardFirstView = () => {
  trackEvent('dashboard_first_view');
};

export const trackRetirementPlanningStart = (source: string) => {
  trackEvent('retirement_planning_start', { source });
};

export const trackWeeklyFeature = (featureName: string) => {
  trackEvent('weekly_feature_usage', { feature: featureName });
};

// Income-specific tracking functions
export const trackAddIncome = (incomeSource: string, amount: number) => {
  trackEvent('income_added', { income_source: incomeSource, amount });
};

export const trackFirstIncomeAdded = (incomeSource: string, amount: number) => {
  markActivationMilestone('income');
  trackEvent('first_income_added', { income_source: incomeSource, amount });
};

export const trackUKTaxYearPlanning = () => {
  trackEvent('uk_tax_year_planning_accessed');
};

// Settings and category tracking functions
export const trackPortfolioSettingsOpen = () => {
  trackEvent('portfolio_settings_opened');
};

export const trackEditAssetCategories = () => {
  trackEvent('asset_categories_edited');
};

export const trackEditIncomeCategories = () => {
  trackEvent('income_categories_edited');
};

export const trackEditExpenseCategories = () => {
  trackEvent('expense_categories_edited');
};

export const trackScenarioModification = (scenarioName: string) => {
  trackEvent('scenario_modified', { scenario_name: scenarioName });
};

// Expense tracking functions
export const trackAddExpense = (expenseCategory: string, amount: number) => {
  trackEvent('expense_added', { expense_category: expenseCategory, amount });
};

export const trackFirstExpenseAdded = (expenseCategory: string, amount: number) => {
  const utmParams = getUTMForAnalytics();
  markActivationMilestone('expense');
  trackEvent('first_expense_added', {
    expense_category: expenseCategory,
    amount,
    ...utmParams
  });
};

// Onboarding Funnel Tracking
let funnelStartTime: number | null = null;
const stepStartTimes: Record<string, number> = {};

export const startFunnel = (funnelName: string) => {
  funnelStartTime = Date.now();
  trackEvent(`${funnelName}_started`, { 
    timestamp: new Date().toISOString(),
    ...getUTMForAnalytics()
  });
};

export const trackFunnelStep = (stepName: string, properties?: Record<string, any>) => {
  const now = Date.now();
  const timeOnStep = stepStartTimes[stepName] ? now - stepStartTimes[stepName] : null;
  
  stepStartTimes[stepName] = now;
  
  trackEvent(stepName, {
    timestamp: new Date().toISOString(),
    time_on_step_ms: timeOnStep,
    time_on_step_seconds: timeOnStep ? Math.round(timeOnStep / 1000) : null,
    total_time_ms: funnelStartTime ? now - funnelStartTime : null,
    total_time_seconds: funnelStartTime ? Math.round((now - funnelStartTime) / 1000) : null,
    ...properties,
    ...getUTMForAnalytics()
  });
};

export const trackWelcomeScreenView = () => {
  trackFunnelStep('welcome_screen_view', { step_number: 1 });
};

export const trackWelcomeScreenAssetFormClick = () => {
  trackFunnelStep('welcome_screen_to_asset_form_click', { step_number: 2 });
};

export const trackWelcomeScreenCompletion = () => {
  trackFunnelStep('welcome_screen_completion', { step_number: 3 });
};

export const trackScenarioCreated = (scenarioName?: string) => {
  const utmParams = getUTMForAnalytics();
  trackEvent('scenario_created', {
    scenario_name: scenarioName || 'default',
    ...utmParams
  });
};

// ============================================================================
// DEL-139: Activation-funnel instrumentation
// Funnel: signup_completed → income_prompt_shown → income_completion →
//         expenses_prompt_shown → expenses_completion → activated
// Per-step abandonment, error capture, and device/browser correlation are
// captured via the `properties` JSONB column on onboarding_progress.
// ============================================================================

export type FunnelStep =
  | 'signup'
  | 'income'
  | 'expenses'
  | 'activated';

interface DeviceContext {
  device_type: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  browser: string;
  os: string;
  viewport_width: number;
  viewport_height: number;
  screen_width: number;
  screen_height: number;
}

let deviceContextMemo: DeviceContext | null = null;

const parseDeviceContext = (): DeviceContext => {
  if (typeof window === 'undefined') {
    return {
      device_type: 'unknown',
      browser: 'unknown',
      os: 'unknown',
      viewport_width: 0,
      viewport_height: 0,
      screen_width: 0,
      screen_height: 0,
    };
  }
  const ua = window.navigator?.userAgent || '';
  const width = window.innerWidth || 0;
  const isMobile = /Mobi|Android|iPhone|iPod/i.test(ua);
  const isTablet = /iPad|Tablet/i.test(ua) && !isMobile;
  const device_type: DeviceContext['device_type'] = isMobile
    ? 'mobile'
    : isTablet
    ? 'tablet'
    : width > 0 && width <= 1024
    ? 'mobile'
    : 'desktop';
  let browser = 'unknown';
  if (/Edg\//.test(ua)) browser = 'edge';
  else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browser = 'chrome';
  else if (/Firefox\//.test(ua)) browser = 'firefox';
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = 'safari';
  let os = 'unknown';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'ios';
  else if (/Windows NT/.test(ua)) os = 'windows';
  else if (/Mac OS X/.test(ua)) os = 'macos';
  else if (/Android/.test(ua)) os = 'android';
  else if (/Linux/.test(ua)) os = 'linux';
  return {
    device_type,
    browser,
    os,
    viewport_width: width,
    viewport_height: window.innerHeight || 0,
    screen_width: window.screen?.width || 0,
    screen_height: window.screen?.height || 0,
  };
};

export const getDeviceContext = (): DeviceContext => {
  if (deviceContextMemo) return deviceContextMemo;
  deviceContextMemo = parseDeviceContext();
  return deviceContextMemo;
};

const funnelStepStartTimes: Record<FunnelStep, number | null> = {
  signup: null,
  income: null,
  expenses: null,
  activated: null,
};

const PROMPT_SUFFIX = '_prompt_shown';
const COMPLETION_SUFFIX = '_completion';
const ERROR_SUFFIX = '_error_shown';
const ABANDONED_SUFFIX = '_abandoned';

export const startFunnelStep = (
  step: FunnelStep,
  extra: Record<string, unknown> = {}
): void => {
  if (typeof window === 'undefined') return;
  funnelStepStartTimes[step] = Date.now();
  const device = getDeviceContext();
  trackOnboardingStep(`${step}${PROMPT_SUFFIX}`, {
    ...extra,
    ...device,
  });
};

export const getFunnelStepElapsedMs = (step: FunnelStep): number | null => {
  const started = funnelStepStartTimes[step];
  if (started == null) return null;
  return Date.now() - started;
};

export const recordFunnelError = (
  step: FunnelStep | string,
  errorMessage: string,
  errorCode?: string
): void => {
  const device = getDeviceContext();
  trackOnboardingStep(`${step}${ERROR_SUFFIX}`, {
    error_message: errorMessage,
    error_code: errorCode ?? null,
    time_on_step_ms: getFunnelStepElapsedMs(step as FunnelStep),
    ...device,
  });
};

export const fireFunnelAbandoned = (
  step: FunnelStep,
  extra: Record<string, unknown> = {}
): void => {
  if (typeof window === 'undefined') return;
  const started = funnelStepStartTimes[step];
  if (started == null) return;
  const elapsed = Date.now() - started;
  // Only fire abandonment when the user spent >30s on the step. Under that
  // threshold, treat as a bounce rather than a considered drop-off.
  if (elapsed < 30_000) return;
  const device = getDeviceContext();
  trackOnboardingStep(`${step}${ABANDONED_SUFFIX}`, {
    time_on_step_ms: elapsed,
    time_on_step_seconds: Math.round(elapsed / 1000),
    ...extra,
    ...device,
  });
  funnelStepStartTimes[step] = null;
};

export const trackFunnelCompletion = (
  step: 'income' | 'expenses' | 'signup',
  extra: Record<string, unknown> = {}
): void => {
  const started = funnelStepStartTimes[step];
  const elapsed = started != null ? Date.now() - started : null;
  const device = getDeviceContext();
  trackOnboardingStep(`${step}${COMPLETION_SUFFIX}`, {
    ...extra,
    time_on_step_ms: elapsed,
    time_on_step_seconds: elapsed != null ? Math.round(elapsed / 1000) : null,
    ...device,
  });
  funnelStepStartTimes[step] = null;
};

export const trackFunnelActivation = (
  activationPath: 'asset+income' | 'asset+expense' | '30yr_projection'
): void => {
  const device = getDeviceContext();
  trackOnboardingStep('activated', {
    activation_path: activationPath,
    ...device,
  });
  funnelStepStartTimes.activated = null;
};

// ============================================================================
// DEL-63: Activation markers + upgrade-trigger tracking
// Activation = (1 asset AND (1 income OR 1 expense)) OR (30yr projection run).
// Markers are written to localStorage when the corresponding `first_*` event
// fires. We never need to clear them - activation is one-way. Server-side
// `users.has_activated` would supersede this if/when the column lands.
// ============================================================================

const ACTIVATION_KEY_PREFIX = 'delphina:activation:';

export type ActivationMilestone = 'asset' | 'income' | 'expense' | '30yr_projection';

export const markActivationMilestone = (milestone: ActivationMilestone): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${ACTIVATION_KEY_PREFIX}${milestone}`, '1');
  } catch {
    // localStorage may throw in private mode - silent fail.
  }
};

const hasActivationMilestone = (milestone: ActivationMilestone): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(`${ACTIVATION_KEY_PREFIX}${milestone}`) === '1';
  } catch {
    return false;
  }
};

export const hasUserActivated = (): boolean => {
  const hasAsset = hasActivationMilestone('asset');
  const hasIncomeOrExpense =
    hasActivationMilestone('income') || hasActivationMilestone('expense');
  const has30yrProjection = hasActivationMilestone('30yr_projection');

  const activated = (hasAsset && hasIncomeOrExpense) || has30yrProjection;

  if (activated && typeof window !== 'undefined') {
    try {
      const alreadyFired = window.localStorage.getItem(
        `${ACTIVATION_KEY_PREFIX}fired`
      ) === '1';
      if (!alreadyFired) {
        window.localStorage.setItem(`${ACTIVATION_KEY_PREFIX}fired`, '1');
        const path: 'asset+income' | 'asset+expense' | '30yr_projection' =
          has30yrProjection
            ? '30yr_projection'
            : hasActivationMilestone('income')
            ? 'asset+income'
            : 'asset+expense';
        trackFunnelActivation(path);
      }
    } catch {
      // localStorage may throw in private mode - silent fail.
    }
  }

  return activated;
};

// Test-only reset hook.
export const __resetActivationMarkers = (): void => {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(ACTIVATION_KEY_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
    deviceContextMemo = null;
    funnelStepStartTimes.signup = null;
    funnelStepStartTimes.income = null;
    funnelStepStartTimes.expenses = null;
    funnelStepStartTimes.activated = null;
  } catch {
    // ignore
  }
};

// Upgrade-trigger instrumentation (DEL-63 §Part 2).
// Three placements per issue scope: 'second_scenario', '30yr_projection',
// 'share_projection'. Shown fires once per placement per user per day;
// clicked fires once per placement per user per day when the user opens
// the upgrade modal via the trigger.
const TRIGGER_SHOWN_DEDUPE = new Set<string>();

const triggerDayKey = (placement: string): string => {
  const day = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `${placement}:${day}`;
};

export type UpgradeTriggerPlacement =
  | 'second_scenario'
  | '30yr_projection'
  | 'share_projection';

interface UpgradeTriggerInput {
  placement: UpgradeTriggerPlacement;
  trigger_id?: string;
  experiment?: string;
  variant?: string;
  source_page?: string;
}

export const trackUpgradeTriggerShown = ({
  placement,
  trigger_id,
  experiment = 'paywall_timing',
  variant = 'control',
  source_page,
}: UpgradeTriggerInput): void => {
  if (typeof window === 'undefined') return;
  const dayKey = triggerDayKey(placement);
  if (TRIGGER_SHOWN_DEDUPE.has(dayKey)) return;
  TRIGGER_SHOWN_DEDUPE.add(dayKey);

  trackEvent('upgrade_trigger_shown', {
    placement,
    trigger_id: trigger_id ?? null,
    experiment,
    variant,
    source_page: source_page ?? window.location.pathname,
  });
};

export const trackUpgradeTriggerClicked = ({
  placement,
  trigger_id,
  experiment = 'paywall_timing',
  variant = 'control',
  source_page,
}: UpgradeTriggerInput): void => {
  if (typeof window === 'undefined') return;
  trackEvent('upgrade_trigger_clicked', {
    placement,
    trigger_id: trigger_id ?? null,
    experiment,
    variant,
    source_page: source_page ?? window.location.pathname,
  });
};

// Test-only reset hook for trigger dedupe.
export const __resetUpgradeTriggerDeduper = (): void => {
  TRIGGER_SHOWN_DEDUPE.clear();
};

// First-30-year-projection marker. Fired the first time the user views a
// 30-year projection (any source). Hooked from FinancialProjection.
export const trackFirst30yrProjectionRun = (source: string): void => {
  if (typeof window === 'undefined') return;
  if (hasActivationMilestone('30yr_projection')) {
    // Already activated by this path; emit the event anyway for analytics,
    // but skip the marker (idempotent).
    trackEvent('30yr_projection_viewed', { source });
    return;
  }
  markActivationMilestone('30yr_projection');
  trackEvent('first_30yr_projection_run', { source });
  trackEvent('30yr_projection_viewed', { source });
};

// ============================================================================
// Paywall hit tracking (DEL-48-B)
// Canonical event name: `paywall_hit`. Required by DEL-48 paid-conversion plan
// for the email nurture sequence. Properties per issue spec:
//   paywall_id, source_page, variant (default "control"),
//   time_to_hit_seconds, user_id (auto-attached server-side).
// Each (paywall_id, opened_session) emits once. The flag is keyed by an
// emit-key (paywall_id + transition_id) so a re-open or page reload can fire
// a fresh hit while a double-mount from React StrictMode only fires once.
// ============================================================================

const FIRST_TOUCH_KEY = 'delphina_first_touch_at';

const getFirstTouchAt = (): number => {
  if (typeof window === 'undefined') return Date.now();
  try {
    const stored = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!Number.isNaN(ts)) return ts;
    }
  } catch {
    // localStorage may throw in private mode - fall through
  }
  const now = Date.now();
  try {
    window.localStorage.setItem(FIRST_TOUCH_KEY, String(now));
  } catch {
    // ignore
  }
  return now;
};

// Track which (paywall_id, session_token) pairs have already fired so dev
// StrictMode double-mount or rapid re-render cannot duplicate the event.
const emittedPaywallHits = new Set<string>();

interface TrackPaywallHitInput {
  paywall_id: string;
  source_page?: string;
  variant?: string;
  // Caller can override the timestamp key for "this hit session" so that
  // opening, closing, and re-opening the modal across one render cycle is
  // distinguishable. Default: derived from paywall_id + current minute.
  emit_key?: string;
}

export const trackPaywallHit = ({
  paywall_id,
  source_page,
  variant = 'control',
  emit_key,
}: TrackPaywallHitInput): void => {
  if (typeof window === 'undefined') return;
  if (!paywall_id) {
    console.warn('[trackPaywallHit] paywall_id is required');
    return;
  }

  const key = emit_key ?? `${paywall_id}:${Math.floor(Date.now() / 60000)}`;
  if (emittedPaywallHits.has(key)) return;
  emittedPaywallHits.add(key);

  const firstTouchAt = getFirstTouchAt();
  const time_to_hit_seconds = Math.max(
    0,
    Math.round((Date.now() - firstTouchAt) / 1000)
  );

  trackEvent('paywall_hit', {
    paywall_id,
    source_page: source_page ?? window.location.pathname,
    variant,
    time_to_hit_seconds,
  });
};

// Test-only reset hook. Not exported on the public tracking API.
export const __resetPaywallHitDeduper = (): void => {
  emittedPaywallHits.clear();
};
